/**
 * Hold keyboard focus inside a dialog, and give it back when the dialog goes.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * Both the tour card and the invitation carried `aria-modal="true"` and neither
 * honoured it. `aria-modal` is a PROMISE to assistive technology: everything
 * outside this element is unavailable. Nothing enforced it, so Tab walked
 * straight out of the card and into the dimmed application behind — a sighted
 * keyboard user could tab onto controls they could not see, and a screen-reader
 * user was told the rest of the page was unavailable while being walked through
 * it. A claim the code did not keep is worse than no claim: it takes a working
 * escape route and labels it a wall.
 *
 * ── THREE THINGS, NOT ONE ──────────────────────────────────────────────────
 *
 * A trap that only handles Tab is the version everybody writes and it leaks:
 *
 *   1. TAB AND SHIFT-TAB WRAP. Off the last control goes to the first, and
 *      backwards off the first goes to the last.
 *   2. FOCUS THAT LANDS OUTSIDE IS PULLED BACK. Tab is not the only way out —
 *      clicking the dim backdrop moves focus to `body`, and after that the
 *      arrow keys and Tab are relative to nothing. A `focusin` listener catches
 *      every route, including the ones no keydown handler sees.
 *   3. FOCUS IS RETURNED. Whatever had focus before the dialog opened gets it
 *      back when the dialog closes. Without this, dismissing the tour drops
 *      the caret at the top of the document and a keyboard user has to tab
 *      back through the whole page to where they were.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ───────────────────────────────────────
 *
 * It does not set `aria-hidden` on the rest of the application, which is the
 * other half of a full modal treatment. It cannot: the tour renders inside the
 * AppShell tree, so hiding the app root would hide the tour with it. Moving the
 * tour into a portal at `document.body` would make that possible and is a
 * bigger change than this one. Stated rather than left as a silent gap — the
 * trap is real, the `aria-hidden` half is not there.
 */

import { useEffect, type RefObject } from 'react';

/**
 * Everything a browser will Tab to, minus the things it will not.
 *
 * `:not([disabled])` and the negative-tabindex exclusion both matter here: the
 * tour's Back button is absent on the first step rather than disabled, but the
 * card itself carries `tabIndex={-1}` so it can be focused programmatically,
 * and including it in the ring would make Tab cycle through the card's own
 * container as though it were a control.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * NO VISIBILITY FILTER, and that is deliberate — it had one and it was wrong.
 *
 * The first version filtered on `el.offsetParent !== null`, the usual shorthand
 * for "is this displayed". It disabled the entire trap: `offsetParent` is null
 * for everything in jsdom, which does no layout, so every test failed at once
 * — and it is ALSO null for any element inside a `position: fixed` subtree in a
 * real browser, which is exactly what both of these dialogs are. When the list
 * comes back empty the Tab handler has nothing to wrap to, so Tab silently does
 * nothing at all.
 *
 * There is nothing to filter anyway. The only hidden thing in either dialog is
 * the launcher's label on a narrow header, and that is a `<span>` — it was
 * never focusable. `:not([disabled])` in the selector covers the real case.
 */
function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
}

/**
 * Trap focus inside `containerRef` while `active`.
 *
 * `restoreTo` is consulted first when the trap closes — the tour uses it to put
 * focus on the "Show me around" button it just taught them about, rather than
 * on whatever they happened to be touching before it opened.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  restoreTo?: () => HTMLElement | null,
): void {
  useEffect(() => {
    if (!active) return;
    if (!containerRef.current) return;

    /*
     * THE CONTAINER IS RESOLVED PER EVENT, NOT CAPTURED ONCE.
     *
     * The first version closed over `containerRef.current` at arm time. The
     * tour's card does not survive the whole tour: while a step's anchor is
     * still being looked for, the spotlight renders a backdrop with no card at
     * all, and React builds a NEW element when it comes back. The captured one
     * was then detached — `contains()` answered false for everything, and
     * `focus()` on a detached node does nothing at all, silently. The trap was
     * still attached and no longer holding anything.
     *
     * Reading the ref each time costs nothing and follows the remount.
     */
    const current = (): HTMLElement | null => containerRef.current;

    const container = containerRef.current;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    /*
     * THE CONTAINER FIRST, WHERE IT CAN TAKE FOCUS — and this is a correction.
     *
     * The first version focused the first real CONTROL, on the reasoning that
     * focusing the container only announces the dialog's label and the first
     * Tab lands on that control anyway, so it was the same destination one
     * keystroke earlier. It is not the same destination. The first control in
     * the tour card is "Skip the tour", and in the invitation it was "No
     * thanks" — so both dialogs opened with a dismissal under the Enter key.
     * Somebody arriving by keyboard could end the tour with the first key they
     * pressed, having read nothing.
     *
     * A dialog must never open with its destructive option focused. So: the
     * container when it carries a `tabindex` (both of these do, precisely so
     * they can hold focus without being a Tab stop), which announces the label
     * and leaves the next Tab to choose. Only when it cannot take focus does
     * this fall back to the first control.
     */
    const initial = container.hasAttribute('tabindex')
      ? container
      : (focusableWithin(container)[0] ?? container);
    initial.focus();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const box = current();
      if (!box) return;
      const items = focusableWithin(box);
      if (items.length === 0) {
        // Nothing to move to, so Tab must not move anything.
        e.preventDefault();
        box.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const focused = document.activeElement;

      if (e.shiftKey && (focused === first || !box.contains(focused))) {
        e.preventDefault();
        last.focus();
        return;
      }
      if (!e.shiftKey && (focused === last || !box.contains(focused))) {
        e.preventDefault();
        first.focus();
      }
    };

    /*
     * The half a keydown handler cannot cover. Clicking the dim backdrop, or a
     * script moving focus, puts the caret outside with no key event to
     * intercept — and from there Tab is relative to the document, not to the
     * dialog, so the next Tab is already loose in the page.
     */
    const onFocusIn = (e: FocusEvent): void => {
      const box = current();
      if (!box) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (box.contains(target)) return;
      const items = focusableWithin(box);
      (box.hasAttribute('tabindex') ? box : (items[0] ?? box)).focus();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn, true);

      /*
       * Give focus back. `isConnected` because the element that had it may have
       * been removed while the dialog was open — the tour navigates between
       * routes, so the control they were on before the tour started is often
       * no longer in the document by the time it ends. Falling through to the
       * body beats throwing, and beats focusing a detached node, which silently
       * does nothing and leaves focus wherever it was.
       */
      const preferred = restoreTo?.() ?? null;
      const target =
        preferred && preferred.isConnected
          ? preferred
          : previouslyFocused && previouslyFocused.isConnected
            ? previouslyFocused
            : null;
      target?.focus();
    };
    // `restoreTo` is read only in the cleanup, and re-running this effect would
    // re-focus the first control mid-dialog — which on the tour means every
    // step change would fight the card's own focus management.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, containerRef]);
}
